"use client";

import { usePathname } from "next/navigation";
import { useFeatureFlags } from "@/lib/contexts/feature-flags-context";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { useMemo } from "react";
import { ClientNavigation } from "./client-navigation";
import { useNavigationCustomization } from "@/lib/hooks/settings/use-navigation-customization";
import { buildNavigationSections } from "./navigation-builder";
import { NavigationRenderer } from "./navigation-renderer";
import { NavigationConfig } from "./types";
import { useSequencesAccess } from "@/lib/features/use-sequences-access";

/**
 * Enhanced Navigation Component
 * 
 * This component is a Client Component because it uses usePathname() and other hooks.
 * However, it imports NavigationRenderer which is a pure Server Component.
 * This separation ensures that client-side state doesn't propagate up the component tree
 * and cause prerendering errors in the root layout.
 * 
 * iPad Support:
 * - Desktop: CSS hover states for menu interactions
 * - iPad: CSS hover states work with touch (no client-side state needed)
 * - Mobile: Drawer-based navigation (handled separately)
 * 
 * Touch targets are optimized for iPad:
 * - min-h-[44px] md:min-h-[48px] lg:min-h-[36px]
 * - Responsive padding for different screen sizes
 */

interface EnhancedNavigationProps {
  className?: string;
}

export function EnhancedNavigation({ className }: EnhancedNavigationProps) {
  const pathname = usePathname() ?? "";
  const { isFeatureEnabled } = useFeatureFlags();
  const { isClientUser, appUser } = useUniversalAuth();
  const roleCheck = useRoleCheck();
  // Frontend-only rollout gate (by email) for the Sequences nav link.
  // See lib/features/sequences-access.ts — not a security boundary.
  const canViewSequences = useSequencesAccess();

  // Memoize default navigation sections to prevent infinite re-renders
  const defaultSections = useMemo(() => {
    const config: NavigationConfig = {
      userType: roleCheck.roleName || "",
      isAdmin: roleCheck.isAdmin,
      userEmail: appUser?.email,
      sensitiveFeatureAccess: appUser?.sensitive_feature_access,
      sections: [], // This will be populated by buildNavigationSections
      featureFlags: {
        canViewDashboard: isFeatureEnabled("canViewDashboard"),
        canViewTickets: isFeatureEnabled("canViewTickets"),
        canViewInternalTickets: isFeatureEnabled("canViewInternalTickets"),
        canCreateTickets: isFeatureEnabled("canCreateTickets"),
        canUseTicketTemplates: isFeatureEnabled("canUseTicketTemplates"),
        showTimeTrackingMenu: isFeatureEnabled("showTimeTrackingMenu"),
        canViewAllTimeEntries: isFeatureEnabled("canViewAllTimeEntries"),
        canCreateTimeEntries: isFeatureEnabled("canCreateTimeEntries"),
        canViewReports: isFeatureEnabled("canViewReports"),
        showClientMenu: isFeatureEnabled("showClientMenu"),
        canViewClients: isFeatureEnabled("canViewClients"),
        canCreateClients: isFeatureEnabled("canCreateClients"),
        showAdminMenu: isFeatureEnabled("showAdminMenu"),
        canViewUsers: isFeatureEnabled("canViewUsers"),
        canManageAutomation: isFeatureEnabled("canManageAutomation"),
        canManageData: isFeatureEnabled("canManageData"),
        canManageSystemSettings: isFeatureEnabled("canManageSystemSettings"),
        // Task Management
        canViewTasks: isFeatureEnabled("canViewTasks"),
        canCreateTasks: isFeatureEnabled("canCreateTasks"),
        canEditTasks: isFeatureEnabled("canEditTasks"),
        canDeleteTasks: isFeatureEnabled("canDeleteTasks"),
        canAssignTasks: isFeatureEnabled("canAssignTasks"),
        canChangeTaskStatus: isFeatureEnabled("canChangeTaskStatus"),
        canChangeTaskPriority: isFeatureEnabled("canChangeTaskPriority"),
        canViewAllTasks: isFeatureEnabled("canViewAllTasks"),
        canViewMyTasks: isFeatureEnabled("canViewMyTasks"),
        canExportTasks: isFeatureEnabled("canExportTasks"),
        // Sales cadence — email-gated rollout (not a role/feature flag).
        canViewSequences,
      },
    };

    return buildNavigationSections(config);
  }, [isFeatureEnabled, roleCheck.isAdmin, roleCheck.roleName, canViewSequences, appUser?.email, appUser?.sensitive_feature_access]);

  const {
    visibleItems,
    hiddenItems,
    allItems,
    saveCustomization,
    resetCustomization,
    toggleItemVisibility,
    moveItemUp,
    moveItemDown,
    isLoaded,
  } = useNavigationCustomization(defaultSections);

  // Don't render until customization is loaded for employee/admin views
  if (!isLoaded && !isClientUser) {
    return null;
  }

           return (
             <>
               {/* Desktop Navigation - Hidden on mobile, shown on tablet and up */}
               <nav className={`hidden md:flex items-center space-x-1 ${className || ""}`}>
                 {isClientUser ? (
                   <ClientNavigation className={className} />
                 ) : (
                   <NavigationRenderer items={visibleItems} pathname={pathname} />
                 )}
               </nav>
             </>
           );
}
